/**
 * Softphone — call state management for the Apex platform.
 *
 * All call operations go through apex-voice via the voice-ws module (cmd.* protocol).
 * Audio is handled by the Twilio Voice SDK (webrtc.ts).
 * This module manages the UI state, cross-tab coordination, and event merging.
 *
 * Architecture:
 * - Dual-slot model: primaryCall (active) + pendingIncoming[] (ringing)
 * - Only the WS leader tab can control calls (cross-tab lock via BroadcastChannel)
 * - SDK drives audio state; apex-voice WS events enrich with metadata
 */

import { apiFetch } from './api';
import { isLeader as isWsLeader, onBecomeLeader } from './leader-election';
import {
  configure as configureVoiceWs,
  connect as connectVoiceWs,
  disconnect as disconnectVoiceWs,
  request as voiceRequest,
  on as voiceOn,
  isConnected as isVoiceConnected,
  isStale as isVoiceStale,
  reconnect as reconnectVoiceWs,
} from './voice-ws';
import { createWebRtcClient, fetchVoiceToken } from './webrtc';
import { trace, debugEmit } from './debug-stream';
import { isAwaitingSupervisorJoin, receiveSupervisorSdkCall } from './supervisor-monitor';
import type { WebRtcClient, WebRtcCall } from './webrtc';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CallPhase = 'dialing' | 'ringing' | 'connected' | 'rejoining' | 'ended';
export type RecordingPhase = 'idle' | 'recording' | 'paused' | 'stopped';

export interface TransferState {
  type: 'cold' | 'warm';
  to: string;
  name?: string;
}

export interface ActiveCall {
  callId: string;
  phase: CallPhase;
  direction: 'inbound' | 'outbound';
  from: string;
  to: string;
  contactName?: string;
  contactId?: string;
  /** True once the rep explicitly picked which contact this caller is, via
   *  the in-call duplicate picker. Lets surfaces stop showing the ambiguous
   *  "N contacts" count and show the chosen name. */
  contactSelected?: boolean;
  /** Populated by the apex-queue WS enrichment listener — apex-voice has no contacts DB. */
  agencyName?: string;
  /** Populated by the apex-queue WS enrichment listener — apex-voice has no contacts DB. */
  brandName?: string;
  held: boolean;
  muted: boolean;
  transfer: TransferState | null;
  startedAt: number;
  answeredAt?: number;
  conferenceName?: string;
}

interface PendingIncoming {
  callId: string;
  callSid?: string;
  from: string;
  to: string;
  contactName?: string;
  /** Transferring agent's display name. Populated only on transfer destination rings. */
  initiatorName?: string;
  contactId?: string;
  agencyName?: string;
  brandName?: string;
  sdkCall?: WebRtcCall;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let primaryCall: ActiveCall | null = null;
let sdkCall: WebRtcCall | null = null;
let recordingState: RecordingPhase = 'idle';
const pendingIncoming: PendingIncoming[] = [];

// Rep-leg rejoin marker: server emits `call.rejoin_required` on WS reconnect
// when an active participant claim still points at a live conference. The
// next SDK incoming call within the rejoin window is the server's re-dial
// — auto-accepted instead of prompting the rep.
//
// Rationale for "first SDK incoming wins": apex-voice keys claims by user,
// so at any moment only one rejoin can be in flight for this rep. A fresh
// inbound arriving in the same ~60s window is rare; if it does, the worst
// case is one mistakenly auto-accepted call which the rep can simply end.
let pendingRejoin: {
  callId: string;
  conferenceName: string;
  timeoutHandle: ReturnType<typeof setTimeout>;
} | null = null;
let rtcClient: WebRtcClient | null = null;
// Mirrors the SDK's "registered" state. apex-voice's executeRejoin only
// dials our identity once we've confirmed Device.register() resolved —
// otherwise Twilio short-circuits the leg to no-answer because no Device
// is listening yet. Cleared when the Device emits "unregistered".
let webRtcRegistered = false;
// Suppresses the unregistered-recovery path while the exported destroy()
// (app shutdown) is intentionally tearing the Device down. Twilio fires
// "unregistered" as part of destroy(), and without this guard the recovery
// would loop on its own teardown. The recovery loop's internal recreate uses
// its own flag (recoveryRecreating) — sharing this one would let an in-flight
// recovery race destroy() and resurrect the Device after a clean shutdown.
let disposing = false;
// Per-pendingRejoin guard: cmd.rejoin_ready is sent at most once per
// pending rejoin window. Two paths can fire it (Device registered after
// rejoin_required arrived, OR rejoin_required arrived after Device was
// already registered) — this flag makes the second call a no-op.
let rejoinReadySent = false;
let initialized = false;
let voiceConfigured = false;
let webRtcInitPromise: Promise<void> | null = null;

// Cross-tab coordination
const callChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('apex-call') : null;

// On-call activity tick — while a call is connected the rep is engaged with
// the phone but may not be touching mouse/keyboard, so the idle tracker's
// `lastActivityAt` can drift past the 15m clock-out threshold. We fire a
// synthetic activity event every 60s while connected/rejoining: a window
// CustomEvent for the same tab and a BroadcastChannel post for sibling tabs
// (whose stale `lastActivityAt` would otherwise overwrite the leader's
// fresh value via the heartbeat HSET). The idle tracker subscribes to both.
const ACTIVITY_TICK_INTERVAL_MS = 60_000;
let activityTickHandle: ReturnType<typeof setInterval> | null = null;

// ---------------------------------------------------------------------------
// UI container
// ---------------------------------------------------------------------------

let container: HTMLElement | null = null;

function getContainer(): HTMLElement {
  if (!container) {
    container = document.createElement('div');
    container.id = 'softphone-root';
    document.body.appendChild(container);
  }
  return container;
}

// ---------------------------------------------------------------------------
// UI status broadcast
//
// The softphone has two independent legs — the apex-voice WebSocket (call
// control, presence) and the Twilio Device (audio path). The header status
// indicator wants a single boolean: both up = green, anything else = red.
// We dispatch this snapshot on a window CustomEvent whenever any leg's state
// changes; the React component listens and rerenders.
// ---------------------------------------------------------------------------

export interface SoftphoneStatus {
  voiceConfigured: boolean;
  wsConnected: boolean;
  wsStale: boolean;
  deviceRegistered: boolean;
  ready: boolean;
}

function snapshotStatus(): SoftphoneStatus {
  const wsConnected = isVoiceConnected();
  const wsStale = isVoiceStale();
  return {
    voiceConfigured,
    wsConnected,
    wsStale,
    deviceRegistered: webRtcRegistered,
    ready: voiceConfigured && wsConnected && !wsStale && webRtcRegistered,
  };
}

function emitSoftphoneStatus(): void {
  window.dispatchEvent(
    new CustomEvent<SoftphoneStatus>('apex:softphone:status', { detail: snapshotStatus() }),
  );
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/** Initialize the softphone. Call once from router.ts initApp(). */
export function initSoftphone(): void {
  if (initialized) return;
  initialized = true;

  // Fetch apex-voice URL from server config and configure voice-ws
  void initVoiceConnection();

  // Tab-foreground re-assert. Browser timer throttling on a backgrounded
  // tab lets our presence (120s server TTL) and device_ready (120s server
  // TTL) entries silently expire. When the tab regains focus, push them
  // both immediately rather than waiting on the next heartbeat tick. Gated
  // on a live WS + a registered Device — sending presence_set for a dead
  // Device would route inbound calls to an identity Twilio can't reach.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (!isWsLeader()) return;
    if (!isVoiceConnected()) return;
    if (!webRtcRegistered) return;
    void voiceRequest('cmd.presence_set', { status: 'available' }).catch(() => {
      // Best-effort — the next heartbeat or _connected handler will retry.
    });
    pushDeviceReady(true);
  });

  // Network came back — don't sit out the rest of a recovery backoff
  // window. Only meaningful on the leader tab (it owns the SDK leg) and
  // only when a Device existed / a recovery is already pending.
  window.addEventListener('online', () => {
    if (!isWsLeader()) return;
    if (webRtcRegistered || disposing) return;
    if (!rtcClient && !deviceRecoveryTimer) return;
    if (deviceRecoveryTimer) {
      clearTimeout(deviceRecoveryTimer);
      deviceRecoveryTimer = null;
    }
    scheduleDeviceRecovery(true);
  });

  // Listen for cross-tab messages
  callChannel?.addEventListener('message', (e) => {
    const msg = e.data as { type: string; call?: ActiveCall; recording?: RecordingPhase };
    if (msg.type === 'call-update' && msg.call) {
      if (!isWsLeader()) {
        primaryCall = msg.call;
        recordingState = msg.recording ?? 'idle';
        renderUI();
        emitCallUpdate();
      }
    }
    if (msg.type === 'call-ended') {
      if (!isWsLeader()) {
        primaryCall = null;
        sdkCall = null;
        recordingState = 'idle';
        renderUI();
        emitCallEnded();
      }
    }
  });

  // Re-init when becoming leader (election completes after initVoiceConnection ran)
  onBecomeLeader(() => {
    if (!voiceConfigured) return;
    if (!isVoiceConnected()) {
      registerVoiceEvents();
      void connectVoiceWs();
    }
    initWebRtcOnGesture();
  });

  // apex-voice now enriches call.ringing / call.answered payloads at emit
  // time via CONTACT_LOOKUP_URL → apex-queue's /internal/contact-card
  // (apex-voice #146). Browser reads contact_id / contact_name /
  // agency_name / brand_name straight from the voice-ws event — no
  // post-arrival patch needed, no flicker.
}

let webRtcGestureScheduled = false;

function initWebRtcOnGesture(): void {
  if (webRtcGestureScheduled) return;
  webRtcGestureScheduled = true;

  const handler = () => {
    document.removeEventListener('click', handler);
    document.removeEventListener('keydown', handler);
    // The user just cleared the autoplay-policy gate. If a rejoin prompt
    // was visible, hide it immediately — even before ensureWebRtc resolves —
    // so the UI doesn't linger while the Device registers.
    hideRejoinPrompt();
    // Failures schedule their own background recovery inside initWebRtc.
    void ensureWebRtc().catch(() => {});
  };

  document.addEventListener('click', handler);
  document.addEventListener('keydown', handler);
}

// Returns true once the Twilio Device has been instantiated. Used by the
// rejoin path to decide whether the rep needs to click before a server
// dialback can land.
function isWebRtcReady(): boolean {
  return rtcClient !== null;
}

function showRejoinPrompt(callId: string, conferenceName: string): void {
  window.dispatchEvent(
    new CustomEvent('apex:rejoin-prompt', {
      detail: { visible: true, callId, conferenceName },
    }),
  );
}

function hideRejoinPrompt(): void {
  window.dispatchEvent(new CustomEvent('apex:rejoin-prompt', { detail: { visible: false } }));
}

// Tells apex-voice that this browser's Twilio Device has finished
// registering and is ready to receive the server's rejoin dial. apex-voice's
// executeRejoin (cmd.rejoin_ready handler) waits for this signal before
// firing AddParticipantToConference — otherwise the dial reaches Twilio
// before the Device has registered against client:<identity> and Twilio
// short-circuits the leg to no-answer (~1s) instead of ringing.
//
// Single-shot per pending rejoin. Both this and the call.rejoin_required
// handler call into here so whichever side wins the race fires the signal.
function sendRejoinReadyIfPending(): void {
  if (!pendingRejoin || !webRtcRegistered || rejoinReadySent) return;
  rejoinReadySent = true;
  debugEmit('softphone', 'sending cmd.rejoin_ready', { callId: pendingRejoin.callId });
  void voiceRequest('cmd.rejoin_ready').catch((err) => {
    debugEmit(
      'softphone',
      'cmd.rejoin_ready failed — server may have already cleared the pending marker',
      { error: String(err) },
      'warn',
    );
  });
}

async function initVoiceConnection(): Promise<void> {
  const t = trace('softphone', 'initVoiceConnection');
  try {
    // Get apex-voice URL from server config
    const resp = await apiFetch('/api/config/voice-url');
    if (!resp.ok) {
      debugEmit('softphone', 'voice not configured', undefined, 'warn');
      t.ok({ configured: false });
      return;
    }
    const { url } = (await resp.json()) as { url: string };
    if (!url) {
      t.ok({ configured: false });
      return;
    }

    voiceConfigured = true;
    emitSoftphoneStatus();

    // Configure voice-ws with token getter
    configureVoiceWs(url, async () => {
      const res = await apiFetch('/auth/voice-token');
      const data = (await res.json()) as { token: string };
      return data.token;
    });

    // Only leader tab connects
    if (isWsLeader()) {
      registerVoiceEvents();
      await connectVoiceWs();
      initWebRtcOnGesture();
    }
    t.ok({ configured: true, leader: isWsLeader() });
  } catch (err) {
    t.fail(err);
  }
}

// device_ready is the orthogonal-to-presence signal that tells apex-voice
// "this user's Twilio Device is currently registered and reachable for a
// `client:<identity>` dial." The server has a 120s TTL on it, so this
// heartbeat refreshes it from the SDK side at 60s — half the TTL, which
// gives one missed heartbeat of slack. setInterval is throttled by the
// browser when the tab is backgrounded, which is the intended mitigation:
// a stale tab silently falls out of routing within ~120s without us
// having to detect every silent-drop scenario explicitly.
const DEVICE_READY_HEARTBEAT_MS = 60_000;
let deviceReadyHeartbeat: ReturnType<typeof setInterval> | null = null;

function pushDeviceReady(ready: boolean): void {
  void voiceRequest('cmd.device_ready', { ready }).catch((err) => {
    // Server may not yet recognize the cmd during the rollout window — log
    // and move on. Once apex-voice is on the matching version this becomes
    // an honest result.
    debugEmit('softphone', 'cmd.device_ready failed', { ready, error: String(err) }, 'warn');
  });
}

function startDeviceReadyHeartbeat(): void {
  if (deviceReadyHeartbeat) return;
  deviceReadyHeartbeat = setInterval(() => {
    if (!webRtcRegistered) {
      stopDeviceReadyHeartbeat();
      return;
    }
    pushDeviceReady(true);
  }, DEVICE_READY_HEARTBEAT_MS);
}

function stopDeviceReadyHeartbeat(): void {
  if (deviceReadyHeartbeat) {
    clearInterval(deviceReadyHeartbeat);
    deviceReadyHeartbeat = null;
  }
}

async function initWebRtc(): Promise<void> {
  const t = trace('softphone', 'initWebRtc');
  if (!isVoiceConnected()) {
    await connectVoiceWs();
    registerVoiceEvents();
  }

  try {
    const { token } = await fetchVoiceToken();
    rtcClient = await createWebRtcClient(token, {
      onRegistered: () => {
        webRtcRegistered = true;
        // Device is healthy again — stop any in-flight recovery and reset
        // the backoff ladder so the next degradation starts fast.
        resetDeviceRecovery();
        emitSoftphoneStatus();
        debugEmit('softphone', 'WebRTC registered');
        // Twilio Device is live — assert it server-side and start the
        // heartbeat that keeps the entry fresh. Without device_ready the
        // transfer-target dropdown was ranking us purely on presence
        // Available, which the WS pong heartbeat keeps alive even when
        // the Device has silently de-registered. Result: cold transfers
        // routed to a dead `client:<identity>` and the contact's leg
        // got cascaded.
        pushDeviceReady(true);
        startDeviceReadyHeartbeat();
        // If apex-voice already told us about a pending rejoin (its
        // markRejoinPending fires from the WS onConnect callback, often
        // before this Device finishes register()), confirm readiness now
        // so the server's executeRejoin actually sees a Device listening
        // for the client:<identity> dial.
        sendRejoinReadyIfPending();
      },
      onUnregistered: () => {
        webRtcRegistered = false;
        emitSoftphoneStatus();
        stopDeviceReadyHeartbeat();
        if (disposing || recoveryRecreating) {
          debugEmit('softphone', 'WebRTC unregistered (disposing)');
          // Even during a clean teardown we want the server to know the
          // identity is no longer reachable — eligibility is computed at
          // the moment of routing, not buffered.
          pushDeviceReady(false);
          return;
        }
        debugEmit('softphone', 'WebRTC unregistered', undefined, 'warn');
        // apex-voice routes inbound dispatch on presence Available; flip
        // ourselves Offline immediately so a leg doesn't fan out to a
        // `client:<identity>` Twilio can't reach during the recovery
        // window. Browser owns the Available flip — re-asserted by
        // onRegistered after recovery succeeds.
        void voiceRequest('cmd.presence_set', { status: 'offline' });
        pushDeviceReady(false);
        scheduleDeviceRecovery();
      },
      onIncoming: handleSdkIncoming,
      onError: (err) => {
        // Defensive: any SDK-surfaced error means we cannot trust the
        // Device to handle an inbound `client:<identity>` dial. Tell the
        // server to skip us until the SDK reports clean state again.
        // Idempotent — pushing false multiple times is safe.
        debugEmit('softphone', 'WebRTC error', { error: String(err) }, 'error');
        pushDeviceReady(false);
        // If the error hit while the Device is not registered (register()
        // failed, token rejected before registration completed, the
        // tokenWillExpire refresh loop in webrtc.ts gave up), Twilio won't
        // necessarily follow up with an "unregistered" event — kick the
        // recovery loop directly. When the Device is still registered this
        // is a call-scoped error and the Device keeps working.
        if (!webRtcRegistered) scheduleDeviceRecovery();
        emitSoftphoneStatus();
      },
    });
  } catch (err) {
    // Couldn't even create the Device (token endpoint down, SDK load
    // failure). Surface to the caller and keep retrying in the background
    // — previously this parked the softphone until a manual page refresh
    // (#1061).
    t.fail(err);
    emitSoftphoneStatus();
    scheduleDeviceRecovery();
    throw err;
  }

  try {
    await rtcClient.register();
    // Set presence to available
    void voiceRequest('cmd.presence_set', { status: 'available' });
    t.ok();
  } catch (err) {
    // Device exists but registration failed (expired token cached from a
    // sleeping laptop, transient network, apex-voice restart). Outbound
    // connect() still works without registration, so don't fail init —
    // schedule recovery so inbound routing comes back without user action.
    t.fail(err);
    debugEmit('softphone', 'WebRTC register failed at init', { error: String(err) }, 'warn');
    emitSoftphoneStatus();
    scheduleDeviceRecovery();
  }
}

/** Await WebRTC readiness — idempotent, deduplicates in-flight init. */
export async function ensureWebRtc(): Promise<void> {
  if (rtcClient) return;
  if (webRtcInitPromise) {
    await webRtcInitPromise;
    return;
  }
  webRtcInitPromise = initWebRtc().finally(() => {
    webRtcInitPromise = null;
  });
  await webRtcInitPromise;
}

// ---------------------------------------------------------------------------
// Device registration recovery (#1061)
//
// Twilio's "unregistered" fires on token expiry, signaling drop, or any
// other event that knocks the Device out of "registered." Per the JS SDK
// best-practices doc the SDK does not auto-recover — the app must call
// register() again. Previously we tried register() exactly once and then
// parked the dead Device until the next user gesture, which left reps with
// a silently broken phone (issue #1061). Now:
//
//   attempt 1..N  — refresh the access token first (an expired/revoked
//                   token is the most common reason a re-register fails),
//                   push it into the Device, and call register() again.
//   attempt N+1.. — tear the Device down and recreate it from scratch via
//                   the normal init path — but ONLY while no call is active.
//                   With a live call (primaryCall / sdkCall) we stay on
//                   register()-only attempts indefinitely: the Twilio Call's
//                   media path can survive brief signaling loss, while
//                   destroying the Device drops the live call outright —
//                   strictly worse. The backoff ladder keeps running
//                   (clamped) and escalation resumes once the line is idle.
//
// Attempts are spaced by DEVICE_RECOVERY_BACKOFF_MS (clamped at the last
// entry) and continue indefinitely — the loop only stops when the Device
// reports "registered", the softphone is destroyed, or a teardown is in
// progress. webRtcRegistered is only ever set by the Device's own
// registered/unregistered events, never by this loop.
//
// Cancellation: resetDeviceRecovery() bumps deviceRecoveryGeneration. An
// in-flight runDeviceRecovery body re-checks its captured generation after
// every await and bails on mismatch — so destroy() (which resets) can never
// race an in-flight attempt into re-initializing the Device after shutdown.
//
// AudioContext note: recovery only ever runs after a Device existed, and
// the first Device is only created post-user-gesture (initWebRtcOnGesture),
// so recreating here stays inside the browser autoplay policy.
// ---------------------------------------------------------------------------

const DEVICE_RECOVERY_BACKOFF_MS = [0, 2_000, 5_000, 10_000, 20_000, 30_000];
/** register()-only attempts before recreating the Device from scratch. */
const DEVICE_RECOVERY_RECREATE_AFTER = 3;

let deviceRecoveryAttempts = 0;
let deviceRecoveryTimer: ReturnType<typeof setTimeout> | null = null;
let deviceRecoveryPromise: Promise<boolean> | null = null;
// Cancellation token for in-flight recovery attempts (see header comment).
let deviceRecoveryGeneration = 0;
// Suppresses the unregistered handler while the recovery loop tears its own
// Device down for a recreate. Deliberately distinct from the shared
// `disposing` flag, which belongs to the exported destroy() — reusing it
// here could clobber an app-shutdown teardown that's racing this loop.
let recoveryRecreating = false;

/**
 * Stop any pending recovery, cancel an in-flight attempt (via the generation
 * bump), and reset the backoff ladder.
 */
function resetDeviceRecovery(): void {
  deviceRecoveryAttempts = 0;
  deviceRecoveryGeneration++;
  if (deviceRecoveryTimer) {
    clearTimeout(deviceRecoveryTimer);
    deviceRecoveryTimer = null;
  }
}

function hasActiveCall(): boolean {
  return primaryCall !== null || sdkCall !== null;
}

function scheduleDeviceRecovery(immediate = false): void {
  if (disposing || webRtcRegistered) return;
  if (deviceRecoveryTimer || deviceRecoveryPromise) return; // pending / in flight
  const idx = Math.min(deviceRecoveryAttempts, DEVICE_RECOVERY_BACKOFF_MS.length - 1);
  const delay = immediate ? 0 : DEVICE_RECOVERY_BACKOFF_MS[idx]!;
  deviceRecoveryTimer = setTimeout(() => {
    deviceRecoveryTimer = null;
    void runDeviceRecovery();
  }, delay);
}

/**
 * One recovery attempt. Returns true when register() resolved (the Device's
 * own "registered" event is what flips webRtcRegistered / resets the
 * backoff). On failure, schedules the next attempt. Deduplicates concurrent
 * callers — a second caller awaits the in-flight attempt.
 */
async function runDeviceRecovery(): Promise<boolean> {
  if (disposing) return false;
  if (webRtcRegistered) return true;
  if (deviceRecoveryPromise) return deviceRecoveryPromise;

  deviceRecoveryAttempts++;
  const attempt = deviceRecoveryAttempts;
  const generation = deviceRecoveryGeneration;
  const cancelled = (): boolean => generation !== deviceRecoveryGeneration;
  deviceRecoveryPromise = (async (): Promise<boolean> => {
    // Call-safety guard: while a call is active, NEVER destroy+recreate —
    // that would drop the live call. Stay on register()-only attempts past
    // the recreate threshold; escalation happens once the line is idle.
    const client = rtcClient;
    if (client && (attempt <= DEVICE_RECOVERY_RECREATE_AFTER || hasActiveCall())) {
      const { token } = await fetchVoiceToken();
      if (cancelled()) return false;
      client.updateToken(token);
      await client.register();
      return true;
    }
    // register() retries exhausted (or the Device never got created) —
    // full teardown + recreate via the normal init path.
    if (rtcClient) {
      recoveryRecreating = true;
      try {
        rtcClient.destroy();
      } finally {
        rtcClient = null;
        recoveryRecreating = false;
      }
    }
    if (cancelled()) return false;
    await ensureWebRtc();
    // No generation re-check needed for the return value: it reads current
    // module state, and a post-destroy() resume sees rtcClient === null.
    return rtcClient !== null && webRtcRegistered;
  })();

  let ok = false;
  try {
    ok = await deviceRecoveryPromise;
  } catch (err) {
    debugEmit(
      'softphone',
      'WebRTC recovery attempt failed',
      { attempt, error: String(err) },
      'warn',
    );
    ok = false;
  } finally {
    deviceRecoveryPromise = null;
  }
  // A cancelled attempt (destroy() or a successful re-register reset the
  // generation) must not reschedule itself — that's the resurrection race.
  if (!ok && !cancelled()) scheduleDeviceRecovery();
  return ok;
}

// ---------------------------------------------------------------------------
// Voice WS event handlers
// ---------------------------------------------------------------------------

let eventsRegistered = false;

function registerVoiceEvents(): void {
  if (eventsRegistered) return;
  eventsRegistered = true;

  // Auto-reconnect succeeded — schedule WebRTC init for the next user
  // gesture. Browsers (correctly) refuse to start the SDK's AudioContext
  // before any user interaction, so creating the Twilio Device here
  // unconditionally would log ~15 "AudioContext was not allowed to start"
  // warnings on every page-load and produce a Device whose audio path is
  // muted on the first incoming call. The leader-only guard still applies
  // because only the leader tab owns the SDK leg.
  voiceOn('_connected', () => {
    emitSoftphoneStatus();
    if (!isWsLeader()) return;
    if (webRtcRegistered) {
      // WS reconnect with a Device that's still registered (Twilio
      // signaling survived the blip). apex-voice's RegisterConnection
      // bootstraps the per-tenant entry as Offline now — only browser-
      // pushed cmd.presence_set flips it to Available — so re-assert
      // immediately to keep this rep in routing.
      debugEmit(
        'softphone',
        'VoiceWS reconnected — Device still registered, re-asserting Available',
      );
      void voiceRequest('cmd.presence_set', { status: 'available' });
      return;
    }
    debugEmit('softphone', 'VoiceWS reconnected — deferring WebRTC init to next gesture');
    initWebRtcOnGesture();
  });

  voiceOn('_disconnected', () => {
    emitSoftphoneStatus();
  });

  voiceOn('_stale', () => {
    emitSoftphoneStatus();
  });

  // apex-voice sends a per-user state seed right after WS upgrade + replay
  // (active calls, presence, participant claim). The softphone is event-
  // driven and `call.rejoin_required` already restores an in-flight call
  // after refresh, so there is no UI seeding to do here. Emit a small
  // shape summary to the debug stream so the snapshot is observable in
  // the toolbelt without leaking the payload — that gives the next
  // consumer (supervisor active-calls view, multi-tab consistency, etc.)
  // a concrete extension point.
  voiceOn('snapshot', (p) => {
    debugEmit('softphone', 'snapshot', {
      active_calls_count: Array.isArray(p['active_calls'])
        ? (p['active_calls'] as unknown[]).length
        : 0,
      has_presence: p['presence'] != null,
      has_participant_claim: p['participant_claim'] != null,
    });
  });

  voiceOn('call.initiated', (p) => {
    if (primaryCall && primaryCall.callId === (p['call_id'] as string)) {
      primaryCall.conferenceName = p['conference_name'] as string | undefined;
    }
  });

  // Server-initiated rejoin after WS reconnect: a stale participant claim
  // points at a still-live conference, server is dialing the rep back in.
  // Mark the auto-accept window and flip any matching primaryCall into a
  // 'rejoining' phase so the UI can show a banner.
  voiceOn('call.rejoin_required', (p) => {
    const callId = p['call_id'] as string | undefined;
    const conferenceName = p['conference_name'] as string | undefined;
    if (!callId || !conferenceName) return;

    debugEmit('softphone', 'call.rejoin_required', {
      call_id: callId,
      conference_name: conferenceName,
    });

    if (pendingRejoin) {
      clearTimeout(pendingRejoin.timeoutHandle);
    }
    rejoinReadySent = false;
    pendingRejoin = {
      callId,
      conferenceName,
      timeoutHandle: setTimeout(() => {
        if (pendingRejoin && pendingRejoin.callId === callId) {
          debugEmit('softphone', 'rejoin window expired without completion', { call_id: callId });
          pendingRejoin = null;
          rejoinReadySent = false;
        }
        // If the rep is still in 'rejoining' state with no completion, treat
        // as call ended so the UI doesn't hang.
        if (primaryCall && primaryCall.callId === callId && primaryCall.phase === 'rejoining') {
          primaryCall.phase = 'ended';
          emitCallUpdate();
        }
        hideRejoinPrompt();
      }, 60_000),
    };

    // If the Device finished register() before this event arrived (e.g.
    // a flaky WS that reconnected without a page refresh, so the SDK is
    // still alive), confirm readiness now. The matching call inside
    // onRegistered handles the more common page-refresh case.
    sendRejoinReadyIfPending();

    if (primaryCall && primaryCall.callId === callId) {
      primaryCall.phase = 'rejoining';
      primaryCall.conferenceName = conferenceName;
    }
    emitCallUpdate();

    // If the Twilio Device hasn't been instantiated yet (refresh on the
    // mid-call tab is the common case), the rep needs to click anywhere
    // before the SDK can accept the server's incoming dialback. Surface
    // a banner so they don't sit silent waiting for "Reconnecting…" to
    // resolve. The banner clears on the very next gesture (handled in
    // initWebRtcOnGesture's gesture handler), and again on rejoin
    // completion / window expiry below.
    if (!isWebRtcReady()) {
      showRejoinPrompt(callId, conferenceName);
    }
  });

  voiceOn('call.rejoin_completed', (p) => {
    const callId = p['call_id'] as string | undefined;
    if (!callId) return;

    debugEmit('softphone', 'call.rejoin_completed', { call_id: callId });

    if (pendingRejoin && pendingRejoin.callId === callId) {
      clearTimeout(pendingRejoin.timeoutHandle);
      pendingRejoin = null;
      rejoinReadySent = false;
    }

    if (primaryCall && primaryCall.callId === callId && primaryCall.phase === 'rejoining') {
      primaryCall.phase = 'connected';
      emitCallUpdate();
    }
    hideRejoinPrompt();
  });

  voiceOn('call.ringing', (p) => {
    const callId = p['call_id'] as string;
    const direction = p['direction'] as string;
    const callSid = p['call_sid'] as string | undefined;
    const from = p['from'] as string | undefined;
    const to = p['to'] as string | undefined;
    const contactName = p['contact_name'] as string | undefined;
    const initiatorName = p['initiator_name'] as string | undefined;
    // Human-readable caller identity (#1049). apex-voice enriches the
    // broadcast call.ringing at emit time via /internal/contact-card —
    // names only, never whole contact records (#653).
    const contactId = p['contact_id'] as string | undefined;
    const agencyName = p['agency_name'] as string | undefined;
    const brandName = p['brand_name'] as string | undefined;
    // Per-agent inbound rings carry `agent_id` and exist only to correlate the
    // SDK incoming Call to a pendingIncoming entry. The leg's own status
    // webhook publishes the display-bearing call.ringing (with `from`,
    // `contact_name`, etc.). The two events race; this flag lets us prefer
    // each event for what it's authoritative on.
    const agentScopedRing = !!p['agent_id'];

    // If this ringing event is for my own outbound call or my active transfer,
    // it's a progress update — not an incoming call. Skip entirely.
    if (primaryCall && primaryCall.callId === callId) {
      debugEmit('softphone', 'call.ringing skipped — own outbound/transfer progress', {
        callId,
        direction,
      });
      return;
    }

    // Backfill any pending entry that arrived via SDK before this WS event.
    // Match any unresolved entry — SDK from/to reflect the agent-ring leg
    // (agency DID → agent client), not the original caller, so we don't try
    // to match on from.
    const unresolved = pendingIncoming.find((c) => c.callId === '');
    if (unresolved) {
      unresolved.callId = callId;
      // Display fields: only the broadcast (non-agent-scoped) call.ringing
      // is authoritative — overwrite whatever the SDK initialized.
      if (!agentScopedRing) {
        if (from) unresolved.from = from;
        if (to) unresolved.to = to;
        if (contactName) unresolved.contactName = contactName;
        if (initiatorName) unresolved.initiatorName = initiatorName;
        if (contactId) unresolved.contactId = contactId;
        if (agencyName) unresolved.agencyName = agencyName;
        if (brandName) unresolved.brandName = brandName;
      }
      // callSid: prefer the per-agent ring's value (the SDK-matching ring leg
      // SID); the broadcast's call_sid is the primary leg's SID.
      if (callSid && (agentScopedRing || !unresolved.callSid)) unresolved.callSid = callSid;
      debugEmit('softphone', 'call.ringing backfilled unresolved entry', { callId });
      renderUI();
      emitCallUpdate();
      emitIncoming();
      return;
    }

    if (direction === 'inbound') {
      const existing = pendingIncoming.find((c) => c.callId === callId);
      if (existing) {
        // Multiple call.ringing events fire for the same call (per-agent
        // correlation + leg-status broadcast). Each event is authoritative
        // for distinct fields: broadcasts carry display data, per-agent
        // rings carry the SDK-matching callSid.
        if (!agentScopedRing) {
          if (from) existing.from = from;
          if (to) existing.to = to;
          if (contactName) existing.contactName = contactName;
          if (initiatorName) existing.initiatorName = initiatorName;
          if (contactId) existing.contactId = contactId;
          if (agencyName) existing.agencyName = agencyName;
          if (brandName) existing.brandName = brandName;
        }
        if (callSid && (agentScopedRing || !existing.callSid)) existing.callSid = callSid;
        debugEmit('softphone', 'call.ringing merged into existing entry', { callId });
        emitIncoming();
      } else {
        // No existing entry yet. Create one — the per-agent ring may land
        // first (in which case from/contactName are empty and get filled in
        // by the broadcast that follows within ms; brief flicker only).
        pendingIncoming.push({
          callId,
          callSid,
          from: agentScopedRing ? '' : (from ?? ''),
          to: agentScopedRing ? '' : (to ?? ''),
          contactName: agentScopedRing ? undefined : contactName,
          initiatorName: agentScopedRing ? undefined : initiatorName,
          contactId: agentScopedRing ? undefined : contactId,
          agencyName: agentScopedRing ? undefined : agencyName,
          brandName: agentScopedRing ? undefined : brandName,
        });
        debugEmit('softphone', 'call.ringing created pendingIncoming', {
          callId,
          direction,
          agentScoped: agentScopedRing,
        });
        renderUI();
        emitCallUpdate();
        emitIncoming();
      }
    } else if (callSid) {
      // Non-inbound direction (e.g., outbound) carrying a callSid. Update
      // existing entry's callSid if missing; otherwise ignore.
      const existingByCallId = pendingIncoming.find((c) => c.callId === callId);
      if (existingByCallId && !existingByCallId.callSid) {
        existingByCallId.callSid = callSid;
        debugEmit('softphone', 'call.ringing updated callSid on existing entry', {
          callId,
          callSid,
        });
        emitIncoming();
      } else {
        debugEmit('softphone', 'call.ringing ignored', { callId, direction });
      }
    }
  });

  voiceOn('call.answered', (p) => {
    const callId = p['call_id'] as string;
    if (primaryCall && primaryCall.callId === callId) {
      primaryCall.phase = 'connected';
      primaryCall.answeredAt = Date.now();
      debugEmit('softphone', 'call.answered — own call connected', { callId });
      renderUI();
      broadcastCallState();
      emitCallUpdate();
      return;
    }

    // Another agent answered — dismiss our pending incoming for this call.
    // Match by callId first; fall back to dismissing the lone unresolved
    // placeholder if no callId match exists. The placeholder case happens
    // when the SDK incoming arrived before the WS call.ringing backfill,
    // and call.ringing was then dropped or arrived after call.answered.
    // Without this fallback the entry would linger in the UI indefinitely.
    let idx = pendingIncoming.findIndex((c) => c.callId === callId);
    if (idx < 0) {
      const unresolved = pendingIncoming.filter((c) => c.callId === '');
      if (unresolved.length === 1) {
        idx = pendingIncoming.indexOf(unresolved[0]!);
      }
    }
    if (idx >= 0) {
      const entry = pendingIncoming[idx]!;
      entry.sdkCall?.reject();
      pendingIncoming.splice(idx, 1);
      debugEmit('softphone', 'call.answered — dismissed pendingIncoming (other agent answered)', {
        callId,
      });
      renderUI();
      emitCallUpdate();
      emitIncoming();
    } else {
      debugEmit('softphone', 'call.answered — no matching call found', { callId });
    }
  });

  voiceOn('call.held', (p) => {
    const callId = p['call_id'] as string | undefined;
    const confName = p['conference_name'] as string | undefined;
    if (
      primaryCall &&
      (primaryCall.callId === callId ||
        (!callId && confName && primaryCall.conferenceName === confName))
    ) {
      primaryCall.held = true;
      renderUI();
      broadcastCallState();
      emitCallUpdate();
    }
  });

  voiceOn('call.unheld', (p) => {
    const callId = p['call_id'] as string | undefined;
    const confName = p['conference_name'] as string | undefined;
    if (
      primaryCall &&
      (primaryCall.callId === callId ||
        (!callId && confName && primaryCall.conferenceName === confName))
    ) {
      primaryCall.held = false;
      renderUI();
      broadcastCallState();
      emitCallUpdate();
    }
  });

  voiceOn('call.muted', (p) => {
    const callId = p['call_id'] as string | undefined;
    const confName = p['conference_name'] as string | undefined;
    if (
      primaryCall &&
      (primaryCall.callId === callId ||
        (!callId && confName && primaryCall.conferenceName === confName))
    ) {
      primaryCall.muted = true;
      renderUI();
      broadcastCallState();
      emitCallUpdate();
    }
  });

  voiceOn('call.unmuted', (p) => {
    const callId = p['call_id'] as string | undefined;
    const confName = p['conference_name'] as string | undefined;
    if (
      primaryCall &&
      (primaryCall.callId === callId ||
        (!callId && confName && primaryCall.conferenceName === confName))
    ) {
      primaryCall.muted = false;
      renderUI();
      broadcastCallState();
      emitCallUpdate();
    }
  });

  voiceOn('call.ended', (p) => {
    const callId = p['call_id'] as string;
    if (primaryCall && primaryCall.callId === callId) {
      debugEmit('softphone', 'call.ended — own call ended', { callId });
      primaryCall.phase = 'ended';
      recordingState = 'idle';
      // End the SDK call immediately — holds the mic / media track open
      // otherwise if Twilio's disconnect event is delayed. The 2s retention
      // below is for UI state, not media.
      sdkCall?.hangup();
      renderUI();
      setTimeout(() => {
        primaryCall = null;
        sdkCall = null;
        renderUI();
        broadcastCallEnded();
        emitCallEnded();
      }, 2000);
    }
    // Same fallback as call.answered: dismiss the lone placeholder when the
    // backfill never arrived, so the incoming-call card doesn't linger.
    let idx = pendingIncoming.findIndex((c) => c.callId === callId);
    if (idx < 0) {
      const unresolved = pendingIncoming.filter((c) => c.callId === '');
      if (unresolved.length === 1) {
        idx = pendingIncoming.indexOf(unresolved[0]!);
      }
    }
    if (idx >= 0) {
      const entry = pendingIncoming[idx]!;
      entry.sdkCall?.reject();
      pendingIncoming.splice(idx, 1);
      debugEmit('softphone', 'call.ended — cleared pendingIncoming', { callId });
      renderUI();
      emitCallUpdate();
      emitIncoming();
    }
  });

  // Recording events
  voiceOn('call.recording_started', (p) => {
    const callId = p['call_id'] as string;
    if (primaryCall && primaryCall.callId === callId) {
      recordingState = 'recording';
      renderUI();
      broadcastCallState();
      emitCallUpdate();
    }
  });

  voiceOn('call.recording_paused', (p) => {
    const callId = p['call_id'] as string;
    if (primaryCall && primaryCall.callId === callId) {
      recordingState = 'paused';
      renderUI();
      broadcastCallState();
      emitCallUpdate();
    }
  });

  voiceOn('call.recording_resumed', (p) => {
    const callId = p['call_id'] as string;
    if (primaryCall && primaryCall.callId === callId) {
      recordingState = 'recording';
      renderUI();
      broadcastCallState();
      emitCallUpdate();
    }
  });

  voiceOn('call.recording_stopped', (p) => {
    const callId = p['call_id'] as string;
    if (primaryCall && primaryCall.callId === callId) {
      recordingState = 'stopped';
      renderUI();
      broadcastCallState();
      emitCallUpdate();
    }
  });

  voiceOn('transfer.initiated', (p) => {
    const callId = p['call_id'] as string;
    if (primaryCall && primaryCall.callId === callId) {
      const transferType = p['transfer_type'] as 'cold' | 'warm';

      if (transferType === 'cold') {
        // Cold transfer = fire and forget. The initiating agent is already
        // removed from the conference — clear call state so the softphone
        // returns to idle and can accept future incoming calls.
        //
        // Explicitly end the SDK call before nulling the reference. Dropping
        // the reference alone leaves the underlying Twilio Device call live
        // (and the mic hot) if Twilio's own disconnect event is delayed or
        // doesn't fire — which is exactly what happens when the server-side
        // leg is removed via conferences API rather than call API.
        debugEmit('softphone', 'transfer.initiated cold — clearing call state', { callId });
        sdkCall?.hangup();
        primaryCall = null;
        sdkCall = null;
        recordingState = 'idle';
        renderUI();
        broadcastCallEnded();
        emitCallEnded();
      } else {
        // Warm transfer — agent stays on the line, show transfer management UI
        debugEmit('softphone', 'transfer.initiated warm — showing transfer UI', {
          callId,
          target: p['target'],
        });
        primaryCall.transfer = {
          type: transferType,
          to: p['target'] as string,
          name: (p['target_name'] as string) || undefined,
        };
        // Clean up any pendingIncoming for this call — transfer means routing is resolved
        for (let i = pendingIncoming.length - 1; i >= 0; i--) {
          if (pendingIncoming[i]!.callId === callId) {
            pendingIncoming[i]!.sdkCall?.reject();
            pendingIncoming.splice(i, 1);
          }
        }
        renderUI();
        broadcastCallState();
        emitCallUpdate();
        emitIncoming();
      }
    }
  });

  voiceOn('transfer.completed', (p) => {
    const callId = p['call_id'] as string;
    // The primaryCall.transfer gate keeps this branch scoped to the initiator
    // of a warm transfer — the target answered with no .transfer field set
    // on their primary, so they correctly pass through unchanged.
    if (primaryCall && primaryCall.callId === callId && primaryCall.transfer) {
      debugEmit('softphone', 'transfer.completed', { callId });
      // Transfer completed — clear initiator's call state so they don't
      // retain a stale primaryCall that blocks future incoming calls.
      // Explicitly end the SDK call before nulling to release mic + media.
      sdkCall?.hangup();
      primaryCall = null;
      sdkCall = null;
      recordingState = 'idle';
      renderUI();
      broadcastCallEnded();
      emitCallEnded();
    }
  });

  voiceOn('transfer.cancelled', (p) => {
    const callId = p['call_id'] as string;
    if (primaryCall && primaryCall.callId === callId) {
      debugEmit('softphone', 'transfer.cancelled', { callId });
      const reason = (p['reason'] as string) || (p['error'] as string) || '';
      primaryCall.transfer = null;
      primaryCall.held = false;
      renderUI();
      broadcastCallState();
      emitCallUpdate();
      showToast(reason ? `Transfer failed — ${reason}` : 'Transfer cancelled');
    }
  });
}

// ---------------------------------------------------------------------------
// SDK incoming handler
// ---------------------------------------------------------------------------

// scheduleDisconnectWatchdog defends against the case where the Twilio SDK
// call transitions to 'disconnected' but the server's `call.ended` WS event
// never arrives (network glitch, server restart, missed webhook, etc).
// Without this the widget sticks forever on a dead call with phase='ended'.
//
// If `call.ended` does arrive (the normal path), it clears primaryCall via
// its 2s setTimeout — this 3s watchdog then finds primaryCall == null or
// mismatched callId and no-ops. Safe to call unconditionally on any
// disconnect transition.
function scheduleDisconnectWatchdog(callId: string): void {
  setTimeout(() => {
    if (primaryCall && primaryCall.callId === callId && primaryCall.phase === 'ended') {
      debugEmit(
        'softphone',
        'SDK disconnect watchdog — no call.ended received, clearing defensively',
        { callId },
      );
      sdkCall?.hangup();
      primaryCall = null;
      sdkCall = null;
      recordingState = 'idle';
      renderUI();
      broadcastCallEnded();
      emitCallEnded();
    }
  }, 3000);
}

function handleSdkIncoming(call: WebRtcCall): void {
  // Supervisor join — auto-accept without showing incoming call UI
  if (isAwaitingSupervisorJoin()) {
    receiveSupervisorSdkCall(call);
    return;
  }

  // Rep-leg rejoin: server sent call.rejoin_required moments ago and is now
  // dialing this rep back into a still-alive conference. The next SDK
  // incoming is that re-dial — auto-accept it. Don't show the incoming-call
  // UI; the rep didn't initiate this and shouldn't be asked to confirm.
  if (pendingRejoin) {
    const rejoin = pendingRejoin;
    debugEmit('softphone', 'auto-accepting rejoin SDK incoming', {
      callSid: call.callSid,
      call_id: rejoin.callId,
      conference_name: rejoin.conferenceName,
    });

    sdkCall = call;
    if (!primaryCall || primaryCall.callId !== rejoin.callId) {
      // No prior primaryCall (refresh wiped state) or it doesn't match —
      // synthesize a minimal one so the UI shows an active call. The
      // rejoin_completed event will flip phase to 'connected' once the
      // server confirms the leg actually joined the conference.
      primaryCall = {
        callId: rejoin.callId,
        phase: 'rejoining',
        direction: 'inbound',
        from: call.from,
        to: call.to,
        held: false,
        muted: false,
        transfer: null,
        startedAt: Date.now(),
        conferenceName: rejoin.conferenceName,
      };
    } else {
      primaryCall.phase = 'rejoining';
    }

    call.accept();

    // Clear pendingIncoming entries that may have been created by a prior
    // race (the SDK incoming sometimes arrives before we wire up state).
    // None should match this call; defensive cleanup only.
    const idx = pendingIncoming.findIndex((p) => p.sdkCall === call);
    if (idx >= 0) pendingIncoming.splice(idx, 1);

    // Mirror the existing pattern: tear down on SDK disconnect.
    call.onStateChange((state) => {
      if (state !== 'disconnected') return;
      if (sdkCall === call) {
        sdkCall = null;
      }
      if (primaryCall && primaryCall.callId === rejoin.callId) {
        primaryCall.phase = 'ended';
      }
      emitCallUpdate();
    });

    renderUI();
    emitCallUpdate();
    emitIncoming();
    return;
  }

  // Match pending entry from WS call.ringing event — callSid is most reliable
  const existing = pendingIncoming.find(
    (p) =>
      !p.sdkCall &&
      ((p.callSid && p.callSid === call.callSid) ||
        p.from === call.from ||
        p.callId === call.callSid),
  );
  if (existing) {
    existing.sdkCall = call;
    debugEmit('softphone', 'SDK incoming matched to pendingIncoming', {
      callSid: call.callSid,
      callId: existing.callId,
    });
  } else {
    // No WS event arrived yet — use empty callId (will be resolved when
    // call.ringing arrives). Never use Twilio SID as callId.
    pendingIncoming.push({
      callId: '',
      from: call.from,
      to: call.to,
      sdkCall: call,
    });
    debugEmit('softphone', 'SDK incoming — no WS match, creating placeholder', {
      callSid: call.callSid,
      from: call.from,
    });
  }

  // Self-dismiss when Twilio cancels this ring leg (the typical path: another
  // agent answered → cancelOtherRingLegs hung us up). Independent of any WS
  // call.answered/ended dismissal — those depend on the server reaching us
  // via a separate channel and matching by callId, both of which can race
  // or be missed. The SDK's own disconnect transition is the SDK-truth
  // signal that the leg is dead and the UI must follow.
  call.onStateChange((state) => {
    if (state !== 'disconnected') return;
    const idx = pendingIncoming.findIndex((p) => p.sdkCall === call);
    if (idx < 0) return;
    pendingIncoming.splice(idx, 1);
    debugEmit('softphone', 'SDK disconnect — dismissed pendingIncoming', {
      callSid: call.callSid,
    });
    renderUI();
    emitCallUpdate();
    emitIncoming();
  });

  renderUI();
  emitCallUpdate();
  emitIncoming();
}

// ---------------------------------------------------------------------------
// Public API — call operations
// ---------------------------------------------------------------------------

export async function dial(
  contactId: string | null,
  contactName: string,
  phone: string,
  fromNumber: string,
): Promise<string | null> {
  if (!isWsLeader()) {
    emitDialError(
      'Calls can only be placed from one tab at a time. Switch to your active tab and try again.',
    );
    return null;
  }
  if (primaryCall) {
    emitDialError('Already on a call. Hang up first to dial again.');
    return null;
  }
  if (!fromNumber) {
    emitDialError('No outbound number selected. Pick a call-capable number first.');
    return null;
  }
  if (!phone) {
    emitDialError('No phone number to dial.');
    return null;
  }

  const t = trace('softphone', 'dial', { to: phone, contact: contactName });
  try {
    // Ensure VoiceWS is connected before dialing — reconnect if needed
    if (!isVoiceConnected()) {
      debugEmit('softphone', 'reconnecting before dial');
      await connectVoiceWs();
    }

    const result = await voiceRequest<{
      id: string;
      conference_name: string;
      call_sid: string;
      from: string;
      to: string;
    }>('cmd.dial', { to: phone, from_number: fromNumber });

    primaryCall = {
      callId: result.id,
      phase: 'dialing',
      direction: 'outbound',
      from: result.from || '',
      to: result.to || phone,
      contactName,
      contactId: contactId ?? undefined,
      held: false,
      muted: false,
      transfer: null,
      startedAt: Date.now(),
      conferenceName: result.conference_name,
    };

    await ensureWebRtc();

    if (rtcClient && result.conference_name) {
      const rtcCallObj = await rtcClient.connect({ conference_name: result.conference_name });
      sdkCall = rtcCallObj;
      rtcCallObj.onStateChange((state) => {
        if (state === 'active' && primaryCall) {
          primaryCall.phase = 'connected';
          if (!primaryCall.answeredAt) primaryCall.answeredAt = Date.now();
        }
        if (state === 'reconnecting' && primaryCall) {
          // SDK signaling/ICE blip — recover within maxCallSignalingTimeoutMs.
          // Reuses the 'rejoining' phase so the existing "Reconnecting…" banner
          // fires for both rep-leg rejoin (#143) and SDK signaling reconnect.
          primaryCall.phase = 'rejoining';
        }
        if (state === 'disconnected' && primaryCall) {
          primaryCall.phase = 'ended';
          scheduleDisconnectWatchdog(primaryCall.callId);
        }
        renderUI();
        broadcastCallState();
        emitCallUpdate();
      });
    }

    renderUI();
    broadcastCallState();
    emitCallUpdate();
    t.ok({ callId: result.id });
    return result.id;
  } catch (err) {
    t.fail(err);
    const message = err instanceof Error ? err.message : 'Unknown error';
    emitDialError(`Couldn't place the call: ${message}`);
    return null;
  }
}

export async function hangup(): Promise<void> {
  if (!primaryCall) return;
  const t = trace('softphone', 'hangup', { callId: primaryCall.callId });

  // Reality-first reconciliation: the Twilio SDK is the source of truth for
  // "is this rep in a conference." If sdkCall is null, the rep has no live
  // call connection — server-side `call:{sid}` state is stale. Clear our
  // primaryCall display locally and skip the server roundtrip; sending
  // cmd.hangup against a SID we're not actually attached to would race the
  // server's stale-call cleanup path and produce noisy logs without doing
  // anything the rep needs.
  if (!sdkCall) {
    debugEmit('softphone', 'hangup: no sdkCall — clearing local state, skipping cmd.hangup', {
      callId: primaryCall.callId,
    });
    primaryCall = null;
    recordingState = 'idle';
    renderUI();
    broadcastCallEnded();
    emitCallEnded();
    t.ok({ path: 'local-only' });
    return;
  }

  try {
    await voiceRequest('cmd.hangup', { call_id: primaryCall.callId });
    t.ok();
  } catch {
    // SDK disconnect as fallback
    t.ok({ fallback: 'sdk' });
  }
  sdkCall?.hangup();
}

export async function answer(): Promise<void> {
  // Prefer the entry with sdkCall attached (the one that matched the Twilio SDK incoming).
  // Fall back to [0] if no entry has sdkCall yet.
  const incoming = pendingIncoming.find((p) => p.sdkCall && p.callId) || pendingIncoming[0];
  if (!incoming) return;
  if (!incoming.callId) {
    debugEmit('softphone', 'answer blocked — callId not yet resolved', undefined, 'warn');
    return;
  }

  debugEmit('softphone', 'answer — selected entry', {
    callId: incoming.callId,
    hasSdkCall: !!incoming.sdkCall,
  });
  const t = trace('softphone', 'answer', { callId: incoming.callId, from: incoming.from });
  try {
    await ensureWebRtc();
  } catch {
    // Best effort — don't block answer if WebRTC init fails
  }

  // Ask server first — only accept SDK call if server approves
  try {
    await voiceRequest('cmd.answer', { call_id: incoming.callId });
  } catch (err) {
    // Server rejected (409 already claimed, or other failure) — do NOT accept SDK call
    debugEmit('softphone', 'answer rejected by server', { callId: incoming.callId, error: err });
    incoming.sdkCall?.reject();
    const idx = pendingIncoming.indexOf(incoming);
    if (idx >= 0) pendingIncoming.splice(idx, 1);
    renderUI();
    emitCallUpdate();
    emitIncoming();
    t.fail(err);
    return;
  }

  // Server approved — now accept the SDK call
  incoming.sdkCall?.accept();

  primaryCall = {
    callId: incoming.callId,
    phase: 'connected',
    direction: 'inbound',
    from: incoming.from,
    to: incoming.to,
    contactName: incoming.contactName,
    contactId: incoming.contactId,
    agencyName: incoming.agencyName,
    brandName: incoming.brandName,
    held: false,
    muted: false,
    transfer: null,
    startedAt: Date.now(),
    answeredAt: Date.now(),
  };
  sdkCall = incoming.sdkCall ?? null;

  // Remove the answered entry and any duplicates for the same call
  const answeredCallId = incoming.callId;
  for (let i = pendingIncoming.length - 1; i >= 0; i--) {
    if (pendingIncoming[i] === incoming || pendingIncoming[i]!.callId === answeredCallId) {
      pendingIncoming.splice(i, 1);
    }
  }
  emitIncoming();

  if (sdkCall) {
    sdkCall.onStateChange((state) => {
      if (state === 'reconnecting' && primaryCall) {
        primaryCall.phase = 'rejoining';
        renderUI();
        broadcastCallState();
        emitCallUpdate();
        return;
      }
      if (state === 'active' && primaryCall && primaryCall.phase === 'rejoining') {
        primaryCall.phase = 'connected';
        renderUI();
        broadcastCallState();
        emitCallUpdate();
        return;
      }
      if (state === 'disconnected' && primaryCall) {
        primaryCall.phase = 'ended';
        scheduleDisconnectWatchdog(primaryCall.callId);
        renderUI();
        broadcastCallState();
        emitCallUpdate();
      }
    });
  }

  renderUI();
  broadcastCallState();
  emitCallUpdate();
  t.ok();
}

export async function reject(): Promise<void> {
  const incoming = pendingIncoming[0];
  if (!incoming) return;

  const t = trace('softphone', 'reject', { callId: incoming.callId });
  incoming.sdkCall?.reject();
  try {
    await voiceRequest('cmd.reject', { call_id: incoming.callId });
    t.ok();
  } catch {
    t.ok({ fallback: true });
  }
  pendingIncoming.shift();
  renderUI();
  emitCallUpdate();
  emitIncoming();
}

/**
 * Set the active call's contact from the in-call duplicate picker. Mutates the
 * shared primaryCall and re-broadcasts so every surface (InCallModal,
 * ActiveCallWidget, other tabs) shows the chosen contact, and marks it
 * rep-confirmed so the widget stops showing the ambiguous "N contacts" count.
 * Display-only — does NOT persist a call↔contact association server-side.
 */
export function setCallContact(contactId: string, contactName: string): void {
  if (!primaryCall) return;
  primaryCall.contactId = contactId;
  primaryCall.contactName = contactName;
  primaryCall.contactSelected = true;
  emitCallUpdate();
  broadcastCallState();
}

export function toggleMute(): void {
  if (!sdkCall || !primaryCall) return;
  if (sdkCall.isMuted()) {
    sdkCall.unmute();
    primaryCall.muted = false;
  } else {
    sdkCall.mute();
    primaryCall.muted = true;
  }
  // Mute is handled entirely by the SDK — local mic gate, instant, no
  // server roundtrip needed. The previous cmd.mute / cmd.unmute path
  // duplicated this with a server-side conference participant mute that
  // matches the SDK's effect for self-mute. Server `call.muted` /
  // `call.unmuted` events still arrive (e.g. when a supervisor mutes
  // someone) and are read-only mirrors for UI.
  debugEmit('softphone', primaryCall.muted ? 'muted' : 'unmuted', {
    callId: primaryCall.callId,
  });
  renderUI();
  broadcastCallState();
  emitCallUpdate();
}

export function isMuted(): boolean {
  return sdkCall?.isMuted() ?? false;
}

export async function toggleHold(): Promise<void> {
  if (!primaryCall) return;
  const newHeld = !primaryCall.held;
  const cmd = newHeld ? 'cmd.hold' : 'cmd.unhold';
  const t = trace('softphone', newHeld ? 'hold' : 'unhold', { callId: primaryCall.callId });
  await voiceRequest(cmd, { call_id: primaryCall.callId });
  t.ok();
  if (!primaryCall) return;
  primaryCall.held = newHeld;
  renderUI();
  broadcastCallState();
  emitCallUpdate();
}

export async function warmTransfer(transferTo: string): Promise<void> {
  if (!primaryCall) return;
  const t = trace('softphone', 'warmTransfer', { callId: primaryCall.callId, to: transferTo });
  try {
    await voiceRequest('cmd.transfer', {
      call_id: primaryCall.callId,
      type: 'warm',
      target: transferTo,
    });
    t.ok();
  } catch (err) {
    t.fail(err);
    throw err;
  }
}

export async function coldTransfer(transferTo: string): Promise<void> {
  if (!primaryCall) return;
  const t = trace('softphone', 'coldTransfer', { callId: primaryCall.callId, to: transferTo });
  try {
    await voiceRequest('cmd.transfer', {
      call_id: primaryCall.callId,
      type: 'cold',
      target: transferTo,
    });
    t.ok();
  } catch (err) {
    t.fail(err);
    throw err;
  }
}

export async function completeWarmTransfer(): Promise<void> {
  if (!primaryCall) return;
  const t = trace('softphone', 'completeWarmTransfer', { callId: primaryCall.callId });
  try {
    await voiceRequest('cmd.transfer_complete', { call_id: primaryCall.callId });
    t.ok();
  } catch (err) {
    t.fail(err);
    throw err;
  }
}

export async function cancelTransfer(): Promise<void> {
  if (!primaryCall) return;
  const t = trace('softphone', 'cancelTransfer', { callId: primaryCall.callId });
  try {
    await voiceRequest('cmd.transfer_cancel', { call_id: primaryCall.callId });
    t.ok();
  } catch (err) {
    t.fail(err);
    throw err;
  }
}

export async function sendDtmf(digits: string): Promise<void> {
  if (!primaryCall) return;
  debugEmit('softphone', 'dtmf', { callId: primaryCall.callId, digits });
  await voiceRequest('cmd.dtmf', { call_id: primaryCall.callId, digits });
}

export async function startRecording(): Promise<void> {
  if (!primaryCall || primaryCall.phase !== 'connected') return;
  if (recordingState === 'recording' || recordingState === 'paused') return;

  const t = trace('softphone', 'startRecording', { callId: primaryCall.callId });
  try {
    await voiceRequest('cmd.recording_start', { call_id: primaryCall.callId });
    recordingState = 'recording';
    renderUI();
    broadcastCallState();
    emitCallUpdate();
    t.ok();
  } catch (err) {
    t.fail(err);
    showToast(err instanceof Error ? err.message : 'Failed to start recording');
  }
}

export async function pauseRecording(): Promise<void> {
  if (!primaryCall || recordingState !== 'recording') return;

  const t = trace('softphone', 'pauseRecording', { callId: primaryCall.callId });
  try {
    await voiceRequest('cmd.recording_pause', { call_id: primaryCall.callId });
    recordingState = 'paused';
    renderUI();
    broadcastCallState();
    emitCallUpdate();
    t.ok();
  } catch (err) {
    t.fail(err);
    showToast(err instanceof Error ? err.message : 'Failed to pause recording');
  }
}

export async function resumeRecording(): Promise<void> {
  if (!primaryCall || recordingState !== 'paused') return;

  const t = trace('softphone', 'resumeRecording', { callId: primaryCall.callId });
  try {
    await voiceRequest('cmd.recording_resume', { call_id: primaryCall.callId });
    recordingState = 'recording';
    renderUI();
    broadcastCallState();
    emitCallUpdate();
    t.ok();
  } catch (err) {
    t.fail(err);
    showToast(err instanceof Error ? err.message : 'Failed to resume recording');
  }
}

export async function stopRecording(): Promise<void> {
  if (!primaryCall || (recordingState !== 'recording' && recordingState !== 'paused')) return;

  const t = trace('softphone', 'stopRecording', { callId: primaryCall.callId });
  try {
    await voiceRequest('cmd.recording_stop', { call_id: primaryCall.callId });
    recordingState = 'stopped';
    renderUI();
    broadcastCallState();
    emitCallUpdate();
    t.ok();
  } catch (err) {
    t.fail(err);
    showToast(err instanceof Error ? err.message : 'Failed to stop recording');
  }
}

export function getRecordingState(): RecordingPhase {
  return recordingState;
}

/** Current composite status of the softphone for UI indicators. */
export function getSoftphoneStatus(): SoftphoneStatus {
  return snapshotStatus();
}

/**
 * Operator-initiated full softphone recovery. Clears any voice-ws stale
 * state, drops the existing socket, reopens it, and re-runs WebRTC init.
 * The caller-supplied gesture (the Reconnect button click) is what unlocks
 * the Twilio Device's AudioContext, so this must be called from a user
 * event handler — never from setInterval or fetch callbacks.
 */
export async function reconnectSoftphone(): Promise<void> {
  if (!voiceConfigured) {
    debugEmit('softphone', 'reconnect skipped — voice not configured', undefined, 'warn');
    return;
  }
  try {
    await reconnectVoiceWs();
  } catch (err) {
    debugEmit('softphone', 'voice-ws reconnect failed', { error: String(err) }, 'error');
    emitSoftphoneStatus();
    throw err;
  }
  // Re-init or re-register the Twilio Device. ensureWebRtc alone is not
  // enough when a client object exists but its Device is unregistered —
  // exactly the state the rep is trying to fix — so route that case
  // through the recovery loop with the backoff ladder reset (this is a
  // deliberate user action, not an automated retry).
  try {
    if (rtcClient && !webRtcRegistered) {
      resetDeviceRecovery();
      const ok = await runDeviceRecovery();
      if (!ok) {
        throw new Error('Phone re-registration failed — retrying in the background');
      }
    } else {
      await ensureWebRtc();
    }
  } catch (err) {
    debugEmit('softphone', 'WebRTC re-init failed', { error: String(err) }, 'error');
    emitSoftphoneStatus();
    throw err;
  }
}

export function getActiveCall(): ActiveCall | null {
  return primaryCall;
}

export function getPendingIncoming(): PendingIncoming[] {
  return [...pendingIncoming];
}

export function showTransferDialog(): void {
  window.dispatchEvent(new CustomEvent('apex:show-transfer-dialog'));
}

// ---------------------------------------------------------------------------
// Cross-tab broadcast
// ---------------------------------------------------------------------------

function broadcastCallState(): void {
  callChannel?.postMessage({ type: 'call-update', call: primaryCall, recording: recordingState });
}

function broadcastCallEnded(): void {
  callChannel?.postMessage({ type: 'call-ended' });
}

function fireActivityTick(): void {
  callChannel?.postMessage({ type: 'activity-tick' });
  window.dispatchEvent(new CustomEvent('apex:activity-tick'));
}

// Drive the on-call activity tick from the current `primaryCall.phase`.
// Idempotent — safe to call from any state-change site. Only the leader
// tab runs the SDK and owns primaryCall mutations, so non-leader callers
// (the cross-tab listener that mirrors primaryCall) short-circuit here.
function syncActivityTick(): void {
  if (!isWsLeader()) {
    if (activityTickHandle) {
      clearInterval(activityTickHandle);
      activityTickHandle = null;
    }
    return;
  }
  const phase = primaryCall?.phase;
  const onCall = phase === 'connected' || phase === 'rejoining';
  if (onCall && !activityTickHandle) {
    fireActivityTick();
    activityTickHandle = setInterval(fireActivityTick, ACTIVITY_TICK_INTERVAL_MS);
  } else if (!onCall && activityTickHandle) {
    clearInterval(activityTickHandle);
    activityTickHandle = null;
  }
}

// ---------------------------------------------------------------------------
// DOM events (for topbar-phone and other UI components)
// ---------------------------------------------------------------------------

function emitCallUpdate(): void {
  // Clone so React's setState sees a new reference. primaryCall is a
  // module-level object that every WS handler mutates in place — dispatching
  // the same reference means React's Object.is returns true and the widget
  // never re-renders past its first state.
  const detail = primaryCall ? { ...primaryCall } : null;
  window.dispatchEvent(new CustomEvent('apex:call-update', { detail }));
  syncActivityTick();
}

function emitCallEnded(): void {
  window.dispatchEvent(new CustomEvent('apex:call-ended'));
  syncActivityTick();
}

function emitIncoming(): void {
  const incoming = pendingIncoming.length > 0 ? { ...pendingIncoming[0]! } : null;
  window.dispatchEvent(new CustomEvent('apex:call-incoming', { detail: incoming }));
}

/**
 * Surface a dial failure to the UI. The softphone library is intentionally
 * UI-agnostic (no toast/sonner imports) — it dispatches an event the app
 * shell can listen to. Every silent-return path in dial() must call this so
 * the rep gets feedback instead of a button click that does nothing (#698).
 */
function emitDialError(reason: string): void {
  window.dispatchEvent(new CustomEvent('apex:dial-error', { detail: { reason } }));
}

// ---------------------------------------------------------------------------
// beforeunload guard
// ---------------------------------------------------------------------------

window.addEventListener('beforeunload', (e) => {
  if (primaryCall && primaryCall.phase !== 'ended') {
    e.preventDefault();
  }
});

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

export function destroy(): void {
  disconnectVoiceWs();
  // Clears any pending recovery timer AND — via the generation bump —
  // cancels an in-flight runDeviceRecovery body: it observes the stale
  // generation after its next await and bails without re-initializing.
  resetDeviceRecovery();
  // disposing suppresses the recovery path for the unregistered event
  // Twilio emits as part of destroy().
  disposing = true;
  try {
    rtcClient?.destroy();
  } finally {
    rtcClient = null;
    disposing = false;
  }
  primaryCall = null;
  sdkCall = null;
  recordingState = 'idle';
  pendingIncoming.length = 0;
  emitIncoming();
  syncActivityTick();
}

// ---------------------------------------------------------------------------
// Transfer toast notification
// ---------------------------------------------------------------------------

function showToast(message: string): void {
  const el = getContainer();
  const toast = document.createElement('div');
  toast.className = 'softphone-toast';
  toast.textContent = message;
  el.appendChild(toast);
  setTimeout(() => toast.remove(), 3500);
}

// ---------------------------------------------------------------------------
// Render — minimal UI (incoming call toast)
// ---------------------------------------------------------------------------

function renderUI(): void {
  const el = getContainer();

  // React app handles incoming call UI via apex:call-incoming event
  const reactMounted = !!document.getElementById('root')?.children.length;

  if (pendingIncoming.length > 0 && !primaryCall && !reactMounted) {
    const incoming = pendingIncoming[0]!;
    el.innerHTML = `
      <div class="softphone-toast softphone-toast--incoming">
        <div class="softphone-toast-title">Incoming Call</div>
        <div class="softphone-toast-from">${esc(incoming.contactName || incoming.from)}</div>
        ${incoming.contactName ? `<div class="softphone-toast-name">${esc(incoming.from)}</div>` : ''}
        <div class="softphone-toast-actions">
          <button class="softphone__btn softphone__btn--answer" onclick="window.ApexSoftphone?.answer()">Answer</button>
          <button class="softphone__btn softphone__btn--reject" onclick="window.ApexSoftphone?.reject()">Reject</button>
        </div>
      </div>
    `;
    return;
  }

  if (!primaryCall) {
    el.innerHTML = '';
    return;
  }

  if (primaryCall.transfer) {
    const t = primaryCall.transfer;
    const targetLabel = esc(t.name || t.to);
    const typeLabel = t.type === 'warm' ? 'Warm transfer' : 'Cold transfer';
    const actions =
      t.type === 'warm'
        ? `<button class="softphone__btn softphone__btn--answer" onclick="window.ApexSoftphone?.completeWarmTransfer()">Complete</button>
           <button class="softphone__btn softphone__btn--reject" onclick="window.ApexSoftphone?.cancelTransfer()">Cancel</button>`
        : `<button class="softphone__btn softphone__btn--reject" onclick="window.ApexSoftphone?.cancelTransfer()">Cancel</button>
           <button class="softphone__btn softphone__btn--hangup" onclick="window.ApexSoftphone?.hangup()">Hangup</button>`;
    el.innerHTML = `
      <div class="softphone-transfer-banner">
        <div>${typeLabel} to ${targetLabel}</div>
        <div style="display:flex;gap:8px;margin-top:6px;">${actions}</div>
      </div>
    `;
    return;
  }

  el.innerHTML = '';
}

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

// ---------------------------------------------------------------------------
// Global API for onclick handlers
// ---------------------------------------------------------------------------

(window as unknown as Record<string, unknown>)['ApexSoftphone'] = {
  dial,
  hangup,
  answer,
  reject,
  toggleMute,
  toggleHold,
  warmTransfer,
  coldTransfer,
  completeWarmTransfer,
  cancelTransfer,
  sendDtmf,
  startRecording,
  pauseRecording,
  resumeRecording,
  stopRecording,
  getRecordingState,
  getActiveCall,
  showTransferDialog,
  setCallContact,
  getSoftphoneStatus,
  reconnectSoftphone,
};
